import React, { useState } from "react";
import * as pdfjsLib from "pdfjs-dist/webpack";
import Tesseract from "tesseract.js";
import { Document, Packer, Paragraph, TextRun } from "docx";

const PdfToWordOCRConverter = () => {
  const [progress, setProgress] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".pdf")) {
      alert("Please select a .pdf file");
      return;
    }

    setLoading(true);
    setProgress("Reading PDF...");

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

      let recognizedText = "";

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        setProgress(`Processing page ${pageNum} of ${pdf.numPages}...`);

        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;

        const imageDataUrl = canvas.toDataURL("image/png");

        // OCR with Tesseract.js
        const result = await Tesseract.recognize(imageDataUrl, "eng", {
          logger: (m) => {
            if (m.status === "recognizing text") {
              setProgress(
                `Page ${pageNum}: ${Math.round(m.progress * 100)}% complete`
              );
            }
          },
        });

        recognizedText += result.data.text.trim() + "\n\n";
      }

      if (!recognizedText.trim()) {
        alert("No text recognized. Try a higher-quality PDF scan.");
        setLoading(false);
        return;
      }

      setProgress("Generating Word file...");

      // Convert recognized text into docx
      const paragraphs = recognizedText
        .split(/\n+/)
        .filter((p) => p.trim())
        .map(
          (p) =>
            new Paragraph({
              children: [new TextRun(p)],
              spacing: { after: 120 },
            })
        );

      const doc = new Document({
        sections: [{ properties: {}, children: paragraphs }],
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name.replace(/\.pdf$/i, "") + "_OCR.docx";
      a.click();
      window.URL.revokeObjectURL(url);

      setProgress("Conversion complete!");
    } catch (error) {
      console.error("OCR conversion failed:", error);
      alert("An error occurred during OCR conversion. See console for details.");
    } finally {
      setLoading(false);
      event.target.value = "";
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>PDF → Word (.docx) OCR Converter</h2>
      <p style={styles.description}>
        Converts scanned/image-based PDFs to editable Word documents using OCR.
      </p>
      <input
        type="file"
        accept=".pdf"
        onChange={handleFileChange}
        style={styles.fileInput}
      />
      {loading && <p style={styles.loading}>{progress}</p>}
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "Arial, sans-serif",
    background: "#fff",
    padding: "40px",
    borderRadius: "10px",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    textAlign: "center",
    width: "90%",
    maxWidth: "500px",
    margin: "100px auto",
  },
  heading: { marginBottom: "15px", color: "#333" },
  description: { color: "#666", fontSize: "14px", marginBottom: "20px" },
  fileInput: {
    border: "2px dashed #007bff",
    padding: "40px 20px",
    borderRadius: "10px",
    cursor: "pointer",
    background: "#f9fbff",
    color: "#007bff",
    transition: "0.3s",
    width: "100%",
  },
  loading: {
    color: "#007bff",
    marginTop: "15px",
    fontSize: "14px",
  },
};

export default PdfToWordOCRConverter;
